/**
 * Informe de conducción de un vehículo.
 *
 * El orden de las secciones no es estético, es el del contrato (CAP-01):
 * primero si los datos sirven, después cuánto se recorrió —que es el
 * denominador de todo lo demás—, y solo entonces los episodios.
 *
 * La advertencia de muestreo va ARRIBA de las cifras, no al pie. Un cero por
 * falta de muestreo se lee como «maneja perfecto» si la explicación aparece
 * después (ADR-007).
 */

import type { AnalysisResponse } from "@core/contracts/api.ts";
import type { DrivingAnalysisV5 } from "@core/pipeline/driving-analysis-v5.ts";
import { Card, Notice, SeverityTag, Stat } from "@web/components/primitives.tsx";
import {
  computeDriveScore,
  COMPONENT_LABELS,
  ACCIDENT_PROBABILITY_POLICY,
  publishesAccidentProbability,
} from "@core/report/drivescore-v1.ts";
import {
  COMPONENT_ORDER,
  countEpisodesByFamily,
  explainNoEpisodes,
  explainRegimeZeros,
  explainUnmeasured,
  measurabilityFromReachability,
  UNMEASURED_NOTICE_TITLE,
} from "@core/report/component-measurability.ts";
import { SCORE_GAUGE_LABELS } from "@core/report/guardia-score.ts";
import { zonedDateTime } from "@core/report/zoned-time.ts";
import {
  ACCIDENT_PROBABILITY_LABEL,
  EN_ACTUALIZACION,
  THEFT_EXPOSURE_LABEL,
} from "@core/report/sections-in-update.ts";

const STATUS_LABEL: Record<string, string> = {
  VALID: "Completo",
  PARTIAL: "Parcial",
  INSUFFICIENT_DATA: "Datos insuficientes",
};

const FAMILY_LABEL: Record<string, string> = {
  acceleration_candidate: "Aceleración",
  braking_candidate: "Frenada",
  left_turn_candidate: "Giro a la izquierda",
  right_turn_candidate: "Giro a la derecha",
  elevated_speed_candidate: "Velocidad elevada",
};

const componentScoreLabel = (value: number | null) =>
  value === null ? "—" : value.toLocaleString("es-CO", { maximumFractionDigits: 1 });

export function DrivingReport({ result }: { result: AnalysisResponse }) {
  // El contrato del motor viaja como objeto opaco por el borde HTTP y aquí se
  // recupera su tipo real: duplicar el esquema Zod entero sería otra copia que
  // mantener sincronizada a mano.
  const analysis = result.analysis as unknown as DrivingAnalysisV5;
  const { sampling, provenance, vehicle, profileSelection } = result;
  const counts = analysis.technicalDriving.severityCounts;
  const moderateOrHigher = counts.moderate + counts.high + counts.critical;

  /*
   * Si se pudo medir cada familia lo decide el SERVIDOR y llega en la respuesta.
   * Esta pantalla lo leía antes por su cuenta, con una copia de la regla, y el
   * informe descargable tenía la suya: dos consumidores que podían discrepar del
   * mismo período. Ahora solo se lee.
   *
   * La ausencia del campo es explícita, no un `?.` escondido: una pestaña
   * abierta desde antes del despliegue recibe la respuesta vieja, y entonces se
   * cae al criterio anterior —alcance físico más episodios encontrados— escrito
   * una sola vez en `measurabilityFromReachability`.
   */
  const measurability =
    result.measurability ??
    measurabilityFromReachability({
      detectors: sampling.detectors,
      episodes: countEpisodesByFamily(analysis.technicalDriving.episodes),
    });
  const familyOf = (detector: string) => measurability.find((item) => item.detector === detector);

  /*
   * El puntaje se calcula aquí, en el navegador, con la misma función pura que
   * usa el informe descargable. Dos sitios que lo calcularan por su cuenta
   * acabarían mostrando números distintos del mismo período.
   *
   * El orden se declara: `COMPONENT_ORDER`, el mismo que usa el informe
   * descargable. El reparto de pesos de `computeDriveScore` desempata por índice
   * cuando dos componentes pierden el mismo decimal, así que el orden del arreglo
   * mueve un 0,1 % de peso — y este llegaba por HTTP. Que hoy el servidor mande la
   * medibilidad en este orden es cierto y no alcanza: un peso del informe no puede
   * depender de en qué orden serializó un arreglo el otro lado del cable.
   */
  const score = computeDriveScore({
    distanceKm: analysis.exposure.eligibleDistanceKm,
    components: [...measurability]
      .sort(
        (a, b) => COMPONENT_ORDER.indexOf(a.component) - COMPONENT_ORDER.indexOf(b.component),
      )
      .map((family) => ({
        component: family.component,
        episodes: family.episodes,
        measured: family.measured,
      })),
  });

  /*
   * Por qué quedó muda cada familia: cadencia o umbral por encima de lo
   * observado. Son dos cosas distintas y el aviso tiene que decir cuál fue.
   *
   * Estas líneas van ARRIBA, en el encabezado, y no en el bloque del puntaje.
   * Antes el aviso de arriba lo disparaba `sampling.detectorsBlindedBySampling`,
   * que solo habla de la cadencia: en el caso real —cadencia de sobra, umbral por
   * encima de lo observado— no aparecía nada arriba y el motivo quedaba en un
   * bloque chico bajo el puntaje… que no se dibuja cuando `driveScore` es `null`.
   * Un período sin puntaje es justo el que más necesita explicar por qué.
   *
   * El informe descargable ya lo pone arriba (ADR-007: un cero que significa «no
   * se pudo medir» se explica ANTES de las cifras, no al pie). Los dos canales
   * publican ahora el mismo aviso, con el mismo título y las mismas líneas.
   */
  const unmeasuredLines = explainUnmeasured(measurability, COMPONENT_LABELS);
  // Ceros contados como medición por la referencia de la cadencia (ADR-009).
  // Van junto al puntaje: son los que explican un 100 con cero episodios.
  const regimeZeroLines = explainRegimeZeros(measurability, COMPONENT_LABELS);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">
            {vehicle.plate ?? vehicle.name ?? vehicle.deviceId}
          </h1>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            {vehicle.group ?? "Sin grupo"} · {zonedDateTime(analysis.period.requestedFrom, analysis.period.timezone)} a{" "}
            {zonedDateTime(analysis.period.requestedTo, analysis.period.timezone)} ({analysis.period.timezone})
          </p>
        </div>
        <span className="rounded-full border border-zinc-300 px-2.5 py-0.5 text-xs font-medium text-zinc-700 dark:border-zinc-700 dark:text-zinc-300">
          {STATUS_LABEL[analysis.status] ?? analysis.status}
        </span>
      </header>

      {result.coverage.truncated ? (
        <Notice tone="warning" title="El período es más largo de lo que cabe en una consulta">
          <p>
            Se alcanzaron a traer {result.coverage.received.toLocaleString("es-CO")} puntos en{" "}
            {result.coverage.pages} páginas antes de agotarse el tiempo, y el análisis solo cubre
            hasta{" "}
            {result.coverage.lastTimestamp ? zonedDateTime(result.coverage.lastTimestamp, analysis.period.timezone) : "—"}. Las cifras de
            abajo describen ese tramo, no el período completo: pide menos días para que sea el
            período que pediste.
          </p>
        </Notice>
      ) : null}

      {unmeasuredLines.length ? (
        <Notice tone="warning" title={UNMEASURED_NOTICE_TITLE}>
          {sampling.explanation ? <p>{sampling.explanation}</p> : null}
          <ul className="mt-2 list-disc space-y-1 pl-4">
            {unmeasuredLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {/*
            El detalle por detector es sobre el ALCANCE FÍSICO a esta cadencia, así
            que solo se muestra cuando la cadencia es parte del problema. Ponerlo
            siempre invitaría a leer «alcanzable» como «se midió», que es
            exactamente la confusión que el caso real explotó: los cuatro
            detectores eran alcanzables y dos no midieron nada.
          */}
          {sampling.detectorsBlindedBySampling ? (
            <ul className="mt-2 space-y-0.5">
              {sampling.detectors.map((detector) => {
                // El alcance es una estimación sobre la cadencia mediana; los
                // episodios son un hecho. Declarar «inalcanzable» una familia
                // que produjo episodios —listados en este mismo informe— es una
                // contradicción que le quita crédito a todo lo demás.
                const encontrados = familyOf(detector.detector)?.episodes ?? 0;
                const alcanzable = detector.reachable || encontrados > 0;
                // Por encima del techo de cadencia lo detectado no es una
                // maniobra: decir «se midieron» sería la contradicción de arriba
                // al revés.
                if (detector.beyondCadenceCeiling) {
                  return (
                    <li key={detector.detector} className="text-xs">
                      <span className="font-medium">{detector.label}:</span> {detector.requirement} · lo
                      detectado no cuenta como medición
                    </li>
                  );
                }
                return (
                  <li key={detector.detector} className="text-xs">
                    <span className="font-medium">{detector.label}:</span> exige{" "}
                    {detector.requirement}{" "}
                    {alcanzable
                      ? encontrados > 0 && !detector.reachable
                        ? `· al límite, pero se midieron ${encontrados}`
                        : "· alcanzable"
                      : "· inalcanzable a esta cadencia"}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </Notice>
      ) : null}

      <Card
        title="Conducción general"
        hint={`DriveScore ${score.version} · pesos y clasificación del manual ${score.manual}. Para cada componente: E = 100 × episodios / km elegibles y S = clamp(100 − E, 0, 100); promedio ponderado 35/25/20/20.`}
      >
        {score.driveScore === null ? (
          <>
            <Notice tone="warning" title="Este período no lleva puntaje">
              {score.explanation}
            </Notice>
            <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Stat label="Distancia" value={score.distanceKm} unit="km" />
              <Stat
                label="Episodios contados"
                value={score.components.reduce((sum, item) => sum + item.episodes, 0)}
              />
              {ACCIDENT_PROBABILITY_POLICY === "EN_ACTUALIZACION" ? (
                <Stat label={ACCIDENT_PROBABILITY_LABEL} value={EN_ACTUALIZACION} />
              ) : null}
              <Stat label={THEFT_EXPOSURE_LABEL} value={EN_ACTUALIZACION} />
            </dl>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <div className="text-5xl font-semibold tabular-nums text-zinc-900 dark:text-zinc-50">
                  {score.driveScore.toLocaleString("es-CO", { minimumFractionDigits: 1 })}
                </div>
                <div className="mt-0.5 text-sm font-medium text-amber-700 dark:text-amber-400">
                  {score.reading?.clientState}
                </div>
              </div>
              <dl className="grid flex-1 grid-cols-2 gap-4 sm:grid-cols-3">
                <Stat
                  label="Riesgo de conducción"
                  value={score.reading?.risk ?? "—"}
                  caveat={
                    publishesAccidentProbability()
                      ? `AccidentProb ${score.reading?.accidentProbability ?? ""}`
                      : undefined
                  }
                />
                <Stat label="Distancia" value={score.distanceKm} unit="km" />
                {ACCIDENT_PROBABILITY_POLICY === "EN_ACTUALIZACION" ? (
                  <Stat label={ACCIDENT_PROBABILITY_LABEL} value={EN_ACTUALIZACION} />
                ) : null}
                <Stat label={THEFT_EXPOSURE_LABEL} value={EN_ACTUALIZACION} />
              </dl>
            </div>

            <div className="mt-4">
              <div className="flex justify-between text-[11px] text-zinc-500 dark:text-zinc-400">
                {SCORE_GAUGE_LABELS.map((label) => (
                  <span key={label}>{label}</span>
                ))}
              </div>
              <div className="relative mt-1 h-2.5 rounded-full bg-gradient-to-r from-red-400 via-amber-400 to-emerald-400 opacity-40">
                <div
                  className="absolute -top-1 w-0.5 rounded bg-zinc-900 dark:bg-zinc-100"
                  style={{ left: `${score.driveScore}%`, height: "1.125rem" }}
                />
              </div>
            </div>

            <p className="mt-3 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
              {score.explanation}
            </p>
            {regimeZeroLines.length ? (
              <ul className="mt-2 list-disc space-y-1 pl-4 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                {regimeZeroLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : null}

            <div className="mt-4 space-y-2 border-t border-zinc-200 pt-4 dark:border-zinc-800">
              <div className="grid grid-cols-[10rem_1fr_3rem_3.5rem_4rem] items-center gap-2 text-[11px] text-zinc-500 dark:text-zinc-400">
                <span>Componente</span>
                <span>Puntos</span>
                <span className="text-right">Eventos</span>
                <span className="text-right">/100 km</span>
                <span className="text-right">Peso</span>
              </div>
              {score.components.map((item) => (
                <div
                  key={item.component}
                  className="grid grid-cols-[10rem_1fr_3rem_3.5rem_4rem] items-center gap-2 text-xs"
                >
                  <span className="text-zinc-600 dark:text-zinc-400">{item.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
                      <span
                        className="block h-full rounded-full bg-brand-600"
                        style={{ width: `${item.score ?? 0}%` }}
                      />
                    </span>
                    <span className="w-16 text-zinc-500">
                      {item.measured ? `${componentScoreLabel(item.score)} puntos` : "sin medición"}
                    </span>
                  </span>
                  <span className="text-right tabular-nums text-zinc-500">
                    {item.measured ? item.episodes : "—"}
                  </span>
                  <span className="text-right tabular-nums text-zinc-500">
                    {item.ratePer100Km === null
                      ? "—"
                      : item.ratePer100Km.toLocaleString("es-CO", { maximumFractionDigits: 1 })}
                  </span>
                  <span className="text-right tabular-nums text-zinc-500">
                    {item.measured ? `${item.effectiveWeight} %` : "0 %"}
                  </span>
                </div>
              ))}
            </div>

            {score.partial ? (
              <div className="mt-3 space-y-1.5 text-xs leading-relaxed text-amber-700 dark:text-amber-500">
                {/*
                  El motivo de cada familia NO se repite aquí: está escrito una vez,
                  en el aviso del encabezado, igual que en el informe descargable.
                  Repetirlo daba dos redacciones del mismo hecho en la misma
                  pantalla, y son justo las que acaban divergiendo.
                */}
                <p>
                  Los {score.unmeasured.length} componente(s) sin medición no valen 100: salen del
                  promedio y su peso se reparte entre los que sí se pudieron medir. Darle nota
                  perfecta a lo que no se midió sería premiar la falta de datos. El motivo de cada
                  uno está en el aviso del encabezado.
                </p>
              </div>
            ) : null}
          </>
        )}
      </Card>

      <Card
        title="Calidad de los datos"
        hint="Qué tan confiable es la base sobre la que se calculó todo lo demás."
      >
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Registros válidos" value={analysis.dataQuality.validPercent} unit="%" />
          <Stat label="Puntos recibidos" value={analysis.dataQuality.received} />
          <Stat
            label="Instantes duplicados"
            value={sampling.duplicateInstants}
            caveat={
              sampling.duplicateInstantPercent && sampling.duplicateInstantPercent > 10
                ? `${sampling.duplicateInstantPercent} % de los intervalos`
                : undefined
            }
          />
          <Stat
            label="En marcha, un punto cada"
            value={sampling.medianIntervalSeconds}
            unit="s"
            caveat={sampling.regime === "LOW" ? "cadencia baja para detectar episodios" : undefined}
          />
        </dl>
      </Card>

      <Card
        title="Exposición"
        hint="El denominador: sin kilómetros no hay tasa que comparar."
      >
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Distancia elegible" value={analysis.exposure.eligibleDistanceKm} unit="km" />
          <Stat
            label="Tiempo cubierto"
            value={analysis.exposure.eligibleDurationHours}
            unit="h"
            // No es tiempo de manejo: el equipo sigue reportando parqueado y
            // esas horas entran igual. Se avisa cuando el promedio lo delata.
            caveat={
              analysis.exposure.eligibleDurationHours > 0 &&
              analysis.exposure.eligibleDistanceKm / analysis.exposure.eligibleDurationHours < 5
                ? "incluye tiempo detenido con el equipo reportando"
                : undefined
            }
          />
          <Stat label="Viajes elegibles" value={analysis.exposure.eligibleTrips} />
          <Stat
            label="Velocidad máxima"
            value={analysis.technicalDriving.maximumValidProviderSpeedKmh}
            unit="km/h"
          />
        </dl>
        {analysis.exposure.eligibleDurationHours > 0 &&
        analysis.exposure.eligibleDistanceKm / analysis.exposure.eligibleDurationHours < 5 ? (
          <p className="mt-3 text-xs text-amber-700 dark:text-amber-500">
            El promedio de{" "}
            {(
              analysis.exposure.eligibleDistanceKm / analysis.exposure.eligibleDurationHours
            ).toFixed(1)}{" "}
            km/h muestra que la mayor parte de ese tiempo el vehículo estuvo detenido reportando.
            Las tasas por 100 km sí son comparables; las tasas por hora quedan diluidas y no
            conviene usarlas para comparar vehículos.
          </p>
        ) : null}
      </Card>

      <Card
        title="Episodios técnicos"
        hint="Señales técnicas consolidadas, no hechos probados sobre el conductor."
      >
        {analysis.technicalDriving.consolidatedEpisodes === 0 ? (
          /*
           * Lo decide `explainNoEpisodes` con la medibilidad, la misma que
           * sostiene el bloque del puntaje de esta pantalla. Aquí había una copia
           * de la regla vieja —solo `detectorsBlindedBySampling`— que en el caso
           * real imprimía «Los detectores sí eran alcanzables con estos datos»
           * mientras el puntaje, arriba, declaraba dos componentes sin medición.
           */
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            {explainNoEpisodes(measurability)}
          </p>
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat label="Episodios" value={analysis.technicalDriving.consolidatedEpisodes} />
              <Stat label="Moderados o más" value={moderateOrHigher} />
              <Stat
                label="Tasa moderados+"
                value={analysis.normalization.moderateOrHigher.per100Km}
                unit="/100 km"
              />
              <Stat label="Altos y críticos" value={counts.high + counts.critical} />
            </dl>
            <table className="mt-4 w-full text-left text-sm">
              <thead className="text-xs text-zinc-500 dark:text-zinc-400">
                <tr>
                  <th className="pb-2 font-medium">Tipo</th>
                  <th className="pb-2 font-medium">Severidad</th>
                  <th className="pb-2 font-medium">Inicio ({analysis.period.timezone})</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {analysis.technicalDriving.episodes.slice(0, 12).map((episode) => (
                  <tr key={episode.id}>
                    <td className="py-1.5">{FAMILY_LABEL[episode.type] ?? episode.type}</td>
                    <td className="py-1.5">
                      <SeverityTag severity={episode.severity} />
                    </td>
                    <td className="py-1.5 tabular-nums text-zinc-600 dark:text-zinc-400">
                      {episode.start ? zonedDateTime(episode.start, analysis.period.timezone) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Card>

      <Card
        title="Procedencia"
        hint="Un informe sin firma no se entrega a un cliente. Dos informes con la misma firma son el mismo informe."
      >
        <dl className="space-y-1.5 text-xs">
          {[
            ["Firma del análisis", provenance.analysisSignature],
            ["Huella de la entrada", provenance.inputDigest],
            [
              // El alcance va aquí y no solo en `reason`: desde que un perfil
              // puede ser de un solo vehículo, «calibrado» ya no dice de dónde
              // salió el umbral, y sin eso dos informes con la misma cadencia y
              // cifras distintas no se pueden explicar.
              "Perfil aplicado",
              `${profileSelection.profileId} · ${
                profileSelection.scope === "VEHICULO"
                  ? `calibrado para este vehículo, v${profileSelection.storedVersion}`
                  : profileSelection.scope === "CADENCIA"
                    ? `calibrado para la cadencia, v${profileSelection.storedVersion}`
                    : "SIN CALIBRAR"
              }`,
            ],
            ["Motor", `${provenance.pipelineVersion} · ${provenance.engineVersion}`],
          ].map(([label, value]) => (
            <div key={label} className="flex flex-wrap gap-x-2">
              <dt className="text-zinc-500 dark:text-zinc-400">{label}:</dt>
              <dd className="break-all font-mono text-zinc-700 dark:text-zinc-300">{value}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">{profileSelection.reason}</p>
        <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
          El umbral de {analysis.profile.elevatedSpeed.operationalSpeedLimitKmh} km/h es operacional de
          laboratorio. No es un límite legal de vía y no implica infracción.
        </p>
      </Card>
    </div>
  );
}
